/**
 * Single-flight access-token refresh shared by both transports (Apollo link +
 * axios interceptor).
 *
 * Access tokens are short-lived (10 min); the rotating refresh token buys a new
 * pair. Concurrent callers must not each rotate — the first rotation invalidates
 * the presented refresh token, so a second concurrent rotation would look like
 * token reuse and revoke the whole family. Everything therefore funnels through
 * ONE in-flight promise that every caller awaits.
 *
 * Two invariants:
 * - **Legacy sessions are untouched.** With no stored refresh token every entry
 *   point returns the stored access token as-is, so behaviour is identical to
 *   before this module existed.
 * - **Impersonation is never refreshed.** An impersonated tab runs on a
 *   tab-scoped `sessionStorage` token; the admin's underlying tokens must not be
 *   rotated from it (see docs/impersonation.md).
 */
import { jwtDecode } from "jwt-decode";

// Deep imports rather than the `@/contexts` barrel — the barrel pulls in every
// provider (and their hooks) and would cycle back through the transports that
// import this module. Same reasoning as `src/api/restClient.ts`.
import {
  clearAccessTokenExpiresAt,
  clearAuthTokens,
  getAccessToken,
  getAccessTokenExpiresAt,
  getRefreshToken,
  setAccessToken,
  setAccessTokenExpiresAt,
  setRefreshToken,
} from "@/contexts/AuthContext/AuthContext.utils";
import { broadcastLogout } from "@/contexts/AuthContext/logoutBroadcast";

import { isAuthApiUnauthorized, refreshTokenPair, type TokenPair } from "./authApi";
import { isImpersonating } from "./impersonation";

/** Refresh this long before the access token actually expires. */
const REFRESH_LEEWAY_MS = 60_000;

let inFlightRefresh: Promise<string | null> | null = null;

/**
 * Persists a freshly issued pair. The refresh token ROTATES on every refresh, so
 * the returned one replaces the stored one; a body without `refreshToken`
 * (cookie mode) leaves the stored one in place. A pair without an expiry hint
 * clears the previous hint so it can't be read against the new token.
 */
export const storeTokenPair = (tokenPair: TokenPair) => {
  setAccessToken(tokenPair.accessToken);

  if (tokenPair.refreshToken) {
    setRefreshToken(tokenPair.refreshToken);
  }

  if (tokenPair.accessTokenExpiresAt) {
    setAccessTokenExpiresAt(tokenPair.accessTokenExpiresAt);
  } else {
    clearAccessTokenExpiresAt();
  }
};

const parseIsoMs = (value: string | null): number | null => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const getJwtExpiryMs = (accessToken: string): number | null => {
  try {
    const { exp } = jwtDecode(accessToken);
    return exp ? exp * 1000 : null;
  } catch {
    return null;
  }
};

const performRefresh = async (refreshToken: string): Promise<string | null> => {
  try {
    const tokenPair = await refreshTokenPair(refreshToken);
    storeTokenPair(tokenPair);
    return tokenPair.accessToken;
  } catch (error) {
    if (isAuthApiUnauthorized(error)) {
      // Expired, revoked or reused refresh token — the session is gone for good.
      clearAuthTokens();
      try {
        broadcastLogout();
      } catch (broadcastError) {
        console.error("Failed to broadcast logout after a rejected refresh:", broadcastError);
      }
      return null;
    }

    // Transient failure (offline, 5xx): keep the session and let the caller
    // proceed with the token it has — a flaky network must not log anyone out.
    console.error("Access token refresh failed:", error);
    return getAccessToken();
  }
};

/**
 * Forces a refresh, joining an already-running one. Returns the new access
 * token, the existing one when the failure was transient, or `null` when the
 * session is over (cells cleared + logout broadcast) or there is nothing to
 * refresh with.
 */
export const refreshAccessToken = async (): Promise<string | null> => {
  if (isImpersonating()) return null;

  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  if (!inFlightRefresh) {
    inFlightRefresh = performRefresh(refreshToken).finally(() => {
      inFlightRefresh = null;
    });
  }

  return inFlightRefresh;
};

/**
 * The token to send with the next request: proactively refreshed when it is
 * within {@link REFRESH_LEEWAY_MS} of expiry. Awaits an in-flight refresh
 * instead of starting a second one.
 */
export const getValidAccessToken = async (): Promise<string | null> => {
  const accessToken = getAccessToken();

  if (isImpersonating()) return accessToken;

  const refreshToken = getRefreshToken();
  if (!refreshToken) return accessToken;

  if (inFlightRefresh) return inFlightRefresh;
  if (!accessToken) return refreshAccessToken();

  const expiresAtMs = parseIsoMs(getAccessTokenExpiresAt()) ?? getJwtExpiryMs(accessToken);

  // Undecodable/opaque token with no expiry hint: nothing to reason about, send
  // it and let a 401 drive the reactive refresh.
  if (expiresAtMs === null) return accessToken;
  if (expiresAtMs - Date.now() > REFRESH_LEEWAY_MS) return accessToken;

  return refreshAccessToken();
};
